import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

export const DARK_MODE = 'darkMode'
const STORAGE_KEY = 'theme'

const readTheme = () => (document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light')

const applyTheme = (theme) => {
  const nextTheme = theme === 'dark' ? 'dark' : 'light'
  const currentTheme = readTheme()
  if (currentTheme === nextTheme) {
    return
  }
  if (nextTheme === 'dark') {
    document.documentElement.dataset.theme = 'dark'
  } else {
    delete document.documentElement.dataset.theme
  }
}

// Before first render. Signed-out pages stay light. Cold-load flash is accepted.
export const applyStoredTheme = () => {
  const signedIn = Boolean(localStorage.getItem('User'))
  applyTheme(signedIn ? localStorage.getItem(STORAGE_KEY) : 'light')
}

export const clearStoredTheme = () => {
  localStorage.removeItem(STORAGE_KEY)
  applyTheme('light')
}

// A store value wins at once; without one, the mirror holds until settings load.
export const useDarkModeSetting = () => {
  const darkMode = useSelector((state) => state.userSettings.settings[DARK_MODE])
  const loaded = useSelector((state) => state.userSettings.loaded)

  useEffect(() => {
    if (darkMode === undefined && !loaded) {
      applyTheme(localStorage.getItem(STORAGE_KEY))
      return
    }
    const theme = darkMode ? 'dark' : 'light'
    localStorage.setItem(STORAGE_KEY, theme)
    applyTheme(theme)
  }, [loaded, darkMode])
}

/**
 * The active color theme, read from the data-theme attribute on <html> and kept in sync
 * when it changes. Use it where a third-party component needs a theme name instead of
 * inheriting CSS variables (Sonner, ECharts).
 * @import import { useTheme } from '@/utils/hooks/useTheme'
 * @returns {'light'|'dark'}
 */
export const useTheme = () => {
  const [theme, setTheme] = useState(readTheme)

  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(readTheme()))
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    })
    return () => observer.disconnect()
  }, [])

  return theme
}
